import React, { useMemo, Suspense, lazy, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import { FooterProvider } from "./components/common/Footer/FooterProvider";
import { RootState } from "./app/store";
import { useClient } from "./hooks/useClient";
import AppSkeleton from "./AppSkeleton";
import { AlertProvider } from "./context/AlertContext";
import { createDashboardTheme } from "./theme/theme";
import ReactGA from "react-ga4";
import { useSyncLanguage } from "./utils/i18n/useSyncLanguage";
import { CssBaseline } from "@mui/material";
import { useAuthWatcher } from "./hooks/useAuthWatcher";

const AppRoutes = lazy(() => import("./utils/AppRoutes/AppRoutes"));

const App: React.FC = () => {
  const publication = useSelector(
    (state: RootState) => state.admin.publicationTheme,
  );

  useSyncLanguage();
  useAuthWatcher();

  const systemTheme = useMemo(() => createDashboardTheme(), []);

  useEffect(() => {
    if (publication && publication.google_analytics_id) {
      const gaId = publication.google_analytics_id;

      ReactGA.initialize(gaId);

      ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname + window.location.search,
      });
    }
  }, [publication]);

  return (
    <ThemeProvider theme={systemTheme}>
      <CssBaseline />
      <AlertProvider>
        <HelmetProvider>
          {publication && publication.google_analytics_id && (
            <Helmet>
              <script
                async
                src={`https://www.googletagmanager.com/gtag/js?id=${publication.google_analytics_id}`}
              ></script>
              <script>
                {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${publication.google_analytics_id}', {
                    page_path: window.location.pathname + window.location.search
                  });
                `}
              </script>
            </Helmet>
          )}
          <Suspense fallback={<AppSkeleton />}>
            <AppRoutes />
          </Suspense>
        </HelmetProvider>
      </AlertProvider>
    </ThemeProvider>
  );
};

const RootApp: React.FC = () => {
  const { magazineLoading } = useClient();

  if (magazineLoading) {
    return <AppSkeleton />;
  }
  return (
    <BrowserRouter
      future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
    >
      <FooterProvider>
        <Suspense fallback={<AppSkeleton />}>
          <App />
        </Suspense>
      </FooterProvider>
    </BrowserRouter>
  );
};

export default RootApp;
